// import { Share2 } from 'lucide-react'
import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import WideButton from '@/components/common/button/WideButton'
import CardOne from '@/components/common/card/CardOne'
import CommonModal from '@/components/common/modal/CommonModal'
import MainTopBar from '@/components/common/bar/MainTopBar'
import { TYPE_RESULT_REPORT } from '@/utils/typeResultReport'
import type { TypeTestResultResponse } from '@/api/model/typeTestType'
import { useUserStore } from '@/store/userStore'
import { useTypeTestStore } from '@/store/typeTestStore'
import { copyClipBoard } from '@/utils/copyClipBoard'
import ToastMessage from '@/components/common/toast/ToastMessage'
import { useUserInfoQuery } from '@/hooks/queries/user'

// 동적 이미지 import
type ImageModule = {
  default: string
}

const typeImages = import.meta.glob<ImageModule>('@/assets/images/types/image-type_*.png', { eager: true })

export default function TypeTestResultPage() {
  const navigate = useNavigate()
  const { userInfo } = useUserStore()
  const { typeResultFrom, setTypeResultFrom } = useTypeTestStore()
  const [showShareModal, setShowShareModal] = useState(false)
  const [showShareToastMessage, setShowShareToastMessage] = useState<{ isVisible: boolean; message: string }>({
    isVisible: false,
    message: ''
  })
  const location = useLocation()
  const typeTestResult = location.state?.typeTestResult as TypeTestResultResponse | undefined
  const { data: userInfoData } = useUserInfoQuery(userInfo.user_id)

  // typeTestResult가 없으면 userInfo의 type16_id 사용, 그것도 없으면 홈으로 리다이렉트
  const userType = typeTestResult?.result?.winners
    ? Math.min(...(typeTestResult.result.winners as number[]))
    : userInfoData?.data.type16_id

  const handleClickButton = () => {
    if (typeResultFrom === 'chat' || typeResultFrom === 'home') {
      navigate('/chat')
    } else {
      navigate('/my')
    }
    setTypeResultFrom('my')
  }

  const handleCopyLinkToastMessage = (isSuccess: boolean) => {
    setShowShareModal(false)
    if (isSuccess) {
      setShowShareToastMessage({ isVisible: true, message: '링크가 성공적으로 복사되었습니다.' })
    } else {
      setShowShareToastMessage({ isVisible: true, message: '링크 복사에 실패했습니다. 다시 한번 시도해주세요. ' })
    }
  }

  // 타입 정보가 없으면 로딩 상태나 빈 화면 표시
  if (!userType) {
    return null
  }

  return (
    <>
      <MainTopBar
        hasBackButton={true}
        handleClickBackButton={() => navigate('/my')}
        leftSide={
          <div className='flex items-center gap-2'>
            <h1 className='text-header-title font-semibold text-text-header'>타입검사</h1>
          </div>
        }
      />
      <div className='min-h-screen items-center justify-center'>
        {/* 메인 콘텐츠 */}
        <div className='px-3 pb-8'>
          {/* 공유 버튼 (임시로 주석처리) */}
          {/* <button className='ml-auto block' onClick={() => setShowShareModal(true)}>
            <Share2 className='w-5 h-5 text-amber-900' />
          </button> */}
          {/* 타입 표시 */}
          <div className='text-center pt-3 pb-6'>
            <h1 className='text-2xl font-bold text-amber-900 mb-2'>TYPE {userType}</h1>
            <p className='text-amber-800 text-sm mb-4'>{TYPE_RESULT_REPORT[userType].typeDescription.split(',')[0]}</p>
            <h2 className='text-3xl font-bold text-amber-900 mb-8'>
              {TYPE_RESULT_REPORT[userType].typeDescription.split(',')[1]}
            </h2>
          </div>

          {/* 캐릭터 이미지 */}
          <div className='flex justify-center mb-8'>
            <div className='relative'>
              <img
                src={typeImages[`/src/assets/images/types/image-type_${userType}.png`].default}
                alt='16_type'
                className={`${userType === 5 || userType === 14 ? 'w-38' : 'w-48'} h-48`}
              />
            </div>
          </div>

          <div className='flex flex-col gap-4 mt-4 mb-8'>
            {TYPE_RESULT_REPORT[userType].contents.map((content, idx) => (
              <CardOne key={idx}>
                {idx > 0 && <h3 className='text-xl font-bold text-gray-800 mb-4 text-center'>{content.title}</h3>}
                <p className='text-gray-700 leading-relaxed text-sm'>{content.description}</p>
              </CardOne>
            ))}
          </div>

          {/* 상담 시작 버튼 */}
          {/* FIXME */}
          <WideButton
            buttonText={typeResultFrom === 'chat' || typeResultFrom === 'home' ? '상담하러 가기' : '확인'}
            handleClick={handleClickButton}
          />
        </div>

        {/* 공유 모달 */}
        {showShareModal && (
          <CommonModal
            title='결과 공유하기'
            topButtonText='카카오톡으로 공유'
            bottomButtonText='링크 복사'
            topButtonOnClick={() => {
              console.log('카카오톡으로 공유')
            }}
            bottomButtonOnClick={() =>
              copyClipBoard(
                window.location.href,
                () => handleCopyLinkToastMessage(true),
                () => handleCopyLinkToastMessage(false)
              )
            }
            onClose={() => {
              setShowShareModal(false)
            }}
          />
        )}
      </div>

      {/* 공유 토스트 메시지 */}
      <ToastMessage
        message={showShareToastMessage.message}
        isVisible={showShareToastMessage.isVisible}
        onClose={() => setShowShareToastMessage({ isVisible: false, message: '' })}
        addClassName='text-center whitespace-pre-line'
        duration={1000}
      />
    </>
  )
}
